import React, { useEffect, useState } from "react";

import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { Button, Divider, IconButton, Stack } from "@mui/material";
import { ColumnOption, resolveSupportedColumns } from "components/DealGroupedTable/dealGroupedTable.config";
import { TableSettings } from "components/DealGroupedTable/TableSettings";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealViews } from "constants/deal";
import { useDealActions, useDealQueriesSelect, usePipelineViewConfigSelector } from "store";

import { PIPELINE_GROUP_DIMENSIONS } from "../../../constants/deal";
import { useGroupingView } from "../GroupingViewContext";
import { GroupBySelector } from "./GroupBySelector";
import { PipelinePropertiesPopup } from "./PipelinePropertiesPopup";

const pillSx = {
  textTransform: "none",
  borderRadius: "20px",
  height: "24px",
  minHeight: "24px",
  fontSize: "13px",
  color: "#0a3142",
  borderColor: "#dae0e2",
  px: "12px",
  whiteSpace: "nowrap"
} as const;

export const PipelineControls: React.FC = () => {
  const {
    updateQueries,
    getCharacteristicsAction,
    updatePipelineViewConfigAction,
    getDealsTableColumnsActions,
    updateTableColumnSettingsAction
  } = useDealActions();
  const {
    view,
    dealListGroupBy,
    tableColumns,
    pipelineGroupBy,
    pipelineCardCharacteristics,
    pipelineCardCharacteristicOptions,
    pipelineHiddenStageIds
  } = useDealQueriesSelect();
  const savedConfig = usePipelineViewConfigSelector();
  const { collapseAll, expandAll, stages } = useGroupingView();

  const isTable = view === DealViews.TABLE;

  // Pipeline: characteristic names for the "Add Characteristic" picker. Table: supported column options.
  const [availableCharacteristics, setAvailableCharacteristics] = useState<string[]>([]);
  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([]);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);

  // Hydrate the config saved in user settings (server-side) into the live deal queries, once on mount.
  useEffect(() => {
    if (savedConfig && Object.keys(savedConfig).length) updateQueries(savedConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isTable) return;
    let active = true;
    getCharacteristicsAction()
      .then((characteristics) => {
        if (!active) return;
        // Card characteristics are name-based, so collapse duplicate names (the account can have several).
        const names = characteristics.map((characteristic) => characteristic.name).filter(Boolean);
        setAvailableCharacteristics([...new Set(names)]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isTable, getCharacteristicsAction]);

  useEffect(() => {
    if (!isTable) return;
    let active = true;
    getDealsTableColumnsActions()
      .then((defs) => {
        if (!active) return;
        setColumnOptions(resolveSupportedColumns(defs).map((column) => ({ key: column.key, label: column.title })));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isTable, getDealsTableColumnsActions]);

  // Persist the WHOLE pipeline config blob (server stores it as one JSON value under the key).
  const persist = (
    patch: Partial<{
      pipelineGroupBy: string[];
      pipelineCardCharacteristics: string[];
      pipelineCardCharacteristicOptions: string[];
      pipelineHiddenStageIds: number[];
    }>
  ) => {
    updatePipelineViewConfigAction({
      pipelineGroupBy: patch.pipelineGroupBy ?? pipelineGroupBy,
      pipelineCardCharacteristics: patch.pipelineCardCharacteristics ?? pipelineCardCharacteristics,
      pipelineCardCharacteristicOptions: patch.pipelineCardCharacteristicOptions ?? pipelineCardCharacteristicOptions,
      pipelineHiddenStageIds: patch.pipelineHiddenStageIds ?? pipelineHiddenStageIds
    }).catch(() => undefined); // best-effort; live state already updated via updateQueries
  };

  const handleGroupByChange = (groupBy: string[]) => {
    if (isTable) {
      updateQueries({ dealListGroupBy: groupBy });
      return;
    }
    updateQueries({ pipelineGroupBy: groupBy });
    persist({ pipelineGroupBy: groupBy });
  };

  const handleApplyProperties = (
    cardCharacteristics: string[],
    cardOptionsList: string[],
    hiddenStageIds: number[]
  ) => {
    updateQueries({
      pipelineCardCharacteristics: cardCharacteristics,
      pipelineCardCharacteristicOptions: cardOptionsList,
      pipelineHiddenStageIds: hiddenStageIds
    });
    persist({
      pipelineCardCharacteristics: cardCharacteristics,
      pipelineCardCharacteristicOptions: cardOptionsList,
      pipelineHiddenStageIds: hiddenStageIds
    });
  };

  const closeSettings = () => setSettingsAnchor(null);

  const groupBy = isTable ? dealListGroupBy : pipelineGroupBy;
  const grouped = groupBy.length > 0;

  return (
    <Stack direction="row" alignItems="center" gap="10px">
      {grouped && (
        <>
          <Button variant="outlined" onClick={collapseAll} sx={pillSx}>
            Collapse All
          </Button>
          <Button variant="outlined" onClick={expandAll} sx={pillSx}>
            Expand All
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "#dae0e2" }} />
        </>
      )}

      <GroupBySelector
        dimensions={PIPELINE_GROUP_DIMENSIONS}
        groupBy={groupBy}
        onChange={handleGroupByChange}
        maxDepth={isTable ? 2 : 1}
      />

      <Divider orientation="vertical" flexItem sx={{ borderColor: "#dae0e2" }} />
      <MaterialTooltip title={isTable ? "Columns" : "Card & stage settings"} isShow>
        <IconButton sx={{ width: "24px", height: "24px" }} onClick={(e) => setSettingsAnchor(e.currentTarget)}>
          <TuneRoundedIcon sx={{ fontSize: "18px", color: "#0a3142" }} />
        </IconButton>
      </MaterialTooltip>

      {isTable ? (
        <TableSettings
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={closeSettings}
          options={columnOptions}
          value={tableColumns}
          onSubmit={updateTableColumnSettingsAction}
        />
      ) : (
        <PipelinePropertiesPopup
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={closeSettings}
          cardOptionsList={pipelineCardCharacteristicOptions}
          availableCharacteristics={availableCharacteristics}
          cardCharacteristics={pipelineCardCharacteristics}
          hiddenStageIds={pipelineHiddenStageIds}
          stages={stages}
          onApply={handleApplyProperties}
        />
      )}
    </Stack>
  );
};
